/**
 * Database Client (browser-safe)
 *
 * Provides a unified interface for database access using Drizzle ORM.
 * This module is safe to import from browser/client-side code — it only
 * uses the Neon HTTP driver (no pg/node-postgres).
 *
 * For server-only (Workers) code that needs pg, import from '@/db/server'.
 *
 * Usage:
 *   import { db } from '@/db/client';
 *   const users = await db.select().from(profiles);
 */

import { drizzle as drizzleHttp } from 'drizzle-orm/neon-http';
import { neon } from '@neondatabase/serverless';
import * as schema from './schema';

// =============================================================================
// Types
// =============================================================================

export interface HyperdriveBinding {
  connectionString: string;
}

// Shared type — both drivers produce compatible Drizzle instances
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DbClient = any;

// =============================================================================
// Browser-safe Client Factory (Neon HTTP)
// =============================================================================

/**
 * Create a Drizzle client for local development
 * Uses direct Neon HTTP connection via DATABASE_URL
 */
export function createDevDb(connectionString?: string): DbClient {
  const url = connectionString || import.meta.env.VITE_DATABASE_URL || process.env.DATABASE_URL;
  if (!url) {
    throw new Error(
      'DATABASE_URL is not set. For local development, run with Doppler: doppler run -- npm run dev'
    );
  }
  const sql = neon(url);
  return drizzleHttp(sql, { schema });
}

// =============================================================================
// Lazy-initialized Default Client (HTTP only, browser-safe)
// =============================================================================

let _db: DbClient | null = null;

/**
 * Default database client for development/SSR
 * Lazily initialized using HTTP driver via DATABASE_URL
 */
export const db = new Proxy({} as DbClient, {
  get(_target, prop) {
    if (!_db) {
      const url = import.meta.env?.VITE_DATABASE_URL || process.env.DATABASE_URL;
      if (!url) {
        throw new Error('Database not available. Run with Doppler: doppler run -- npm run dev');
      }
      const sql = neon(url);
      _db = drizzleHttp(sql, { schema });
    }
    return (_db as Record<string, unknown>)[prop as string];
  },
});

// =============================================================================
// Re-exports for convenience
// =============================================================================

export * from './schema';
export { eq, and, or, desc, asc, sql, count, inArray, like, ilike } from 'drizzle-orm';
